import React from 'react';

// Helper function to format union type display
const formatUnionType = (unionType: string | undefined | null): string => {
  if (!unionType) return 'Not specified';
  switch (unionType) {
    case 'union':
      return 'Union';
    case 'clac':
      return 'CLAC';
    case 'merit':
      return 'Merit';
    case 'other':
      return 'Unrepresented';
    default:
      return 'Not specified';
  }
};

interface TradeType {
    id: string;
    category: 'UA' | 'NON-UA';
    name: string;
    description: string | null;
    createdAt: string;
    updatedAt: string;
}

interface Contractor {
    id: string;
    name: string;
    email: string;
    cellNumber: string | null;
    isSignatory: boolean;
    isMultiTrade: boolean;
    status: string;
    additionalNotes: string | null;
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    contractorUnionType: string | null;
}

interface LocalUnion {
    id: string;
    code: string;
    unionNumber: number;
    createdAt: string;
    updatedAt: string;
}

interface TradeContractAssociation {
    id: string;
    noOfJourneymen: number;
    noOfApperntices: number;
    localUnion?: LocalUnion;
    unionType?: 'union' | 'clac' | 'merit' | 'other';
    createdAt: string;
    updatedAt: string | null;
    contractor: Contractor;
    tradeType: TradeType;
}

interface JobTrade {
    id: string;
    unionStatus: 'union' | 'clac' | 'merit' | 'other' | 'mixed';
    isSignatory: boolean;
    numOfWorkers: number;
    isUACanadaTrade: boolean;
    createdAt: string;
    updatedAt: string | null;
    tradeType: TradeType;
    localUnionIds: Array<{
        id: string;
        code: string;
        unionNumber: number;
        createdAt: string;
        updatedAt: string;
    }>;
    tradeContractAssociations: TradeContractAssociation[];
}

interface JobsiteTradeResponse {
    id: string;
    noOfTrades: string;
    createdAt: string;
    updatedAt: string | null;
    jobTrade: JobTrade[];
}

interface TradesSectionProps {
    trades: JobsiteTradeResponse | null;
}

const TradesSection: React.FC<TradesSectionProps> = ({ trades }) => {
    // Get unique trade types from jobTrade array
    const uniqueTradeTypes = trades?.jobTrade?.map(trade => trade.tradeType.name) || [];
    const tradeTypeNames = uniqueTradeTypes.length > 0 ? uniqueTradeTypes.join(', ') : '-';

    // Group associations by contractor -> local -> trade
    const groupByContractorLocalTrade = () => {
        const grouped: { [contractorId: string]: {
            contractorName: string;
            locals: { [localId: string]: {
                localName: string;
                trades: {
                    tradeName: string;
                    unionType: string | null;
                    journeymen: number;
                    apprentices: number
                }[]
            }}
        }} = {};

        trades?.jobTrade?.forEach((trade) => {
            trade.tradeContractAssociations.forEach((association) => {
                const contractorId = association.contractor.id;
                const contractorName = association.contractor.name;
                const localId = association.localUnion?.id || 'no-local';
                const localName = association.localUnion
                    ? `Local ${association.localUnion.unionNumber}`
                    : 'Not specified';
                const unionType = association.unionType || null;

                if (!grouped[contractorId]) {
                    grouped[contractorId] = {
                        contractorName,
                        locals: {}
                    };
                }

                if (!grouped[contractorId].locals[localId]) {
                    grouped[contractorId].locals[localId] = {
                        localName,
                        trades: []
                    };
                }

                grouped[contractorId].locals[localId].trades.push({
                    tradeName: trade.tradeType.name,
                    unionType: unionType,
                    journeymen: association.noOfJourneymen,
                    apprentices: association.noOfApperntices
                });
            });
        });

        return grouped;
    };

    const groupedData = groupByContractorLocalTrade();

    return (
        <div className='bg-white mb-4'>
            <div className='p-4 sm:p-6'>
                <h2 className='text-[20px] font-[700] mb-3'>Trades</h2>
                <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'>
                    <div>
                        <p className='text-[16px] font-[700] text-black'>No. Trades seen on site</p>
                        <p className='text-[16px] font-[500]'>{trades?.noOfTrades || '-'}</p>
                    </div>
                    <div>
                        <p className='text-[16px] font-[700] text-black'>Trade Types</p>
                        <p className='text-[16px] font-[500]'>{tradeTypeNames}</p>
                    </div>
                </div>

                {/* Mechanical Contractors and Trade Details */}
                <div className="col-span-2 mt-4">
                    <p className='text-[16px] font-[700] text-black mb-2'>Mechanical Contractors</p>
                    {Object.keys(groupedData).length > 0 ? (
                        <div className="space-y-4">
                            {Object.entries(groupedData).map(([contractorId, contractorData]) => (
                                <div key={contractorId} className="border rounded-lg p-4">
                                    <h3 className="text-[16px] font-[600] text-black mb-3">{contractorData.contractorName}</h3>

                                    {Object.entries(contractorData.locals).map(([localId, localData]) => (
                                        <div key={localId} className="ml-4 mb-3 last:mb-0">
                                            <p className="text-[14px] font-[600] text-gray-700 mb-2">{localData.localName}</p>

                                            <div className="ml-4 space-y-2">
                                                {localData.trades.map((trade, idx) => (
                                                    <div key={idx} className="bg-gray-50 p-3 rounded">
                                                        <p className="text-[14px] font-[600] text-black mb-1">{trade.tradeName}</p>
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[13px] text-gray-600">
                                                            <div>
                                                                <span className="font-[600]">Journeymen:</span> {trade.journeymen}
                                                            </div>
                                                            <div>
                                                                <span className="font-[600]">Apprentices:</span> {trade.apprentices}
                                                            </div>
                                                            <div>
                                                                <span className="font-[600]">Union Status:</span> {formatUnionType(trade.unionType)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className='text-[16px] font-[500]'>No contractors recorded</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TradesSection;